import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import type { Resource } from "@/lib/types.js";

export function ResourcesPage(): React.JSX.Element {
  const { api, me } = useTenant();
  const { toast } = useToast();
  const resources = useAsync(() => api.listResources(), [api]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Tenant-level catalog lock: the CRM owns the whole catalog once a sync has
  // applied (catalog_version > 0). Authoring UI is hidden — the API rejects
  // those writes with 409 catalog_managed_by_crm anyway.
  const managedByCrm = me?.tenant.catalog_managed_by_crm === true;

  const visible = (resources.data?.data ?? []).filter((r) => !r.deleted_at);
  useKeyboardShortcuts({
    c: () => {
      if (!managedByCrm) setCreating(true);
    },
    j: () => setSelectedId(moveSelection(visible, selectedId, 1)),
    k: () => setSelectedId(moveSelection(visible, selectedId, -1)),
    e: () => {
      if (managedByCrm) return;
      const row = visible.find((r) => r.id === selectedId);
      if (row) setEditing(row);
    }
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Resources</h1>
        {!managedByCrm && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
        )}
      </div>
      {/*
        PRD principal-scoped-reads-and-crm-refs (REQ-PSR-010). CRM owns
        employee identity; this page edits the local resource record (rooms,
        equipment, the bookable-entity layer of the resource model). Renaming
        a stylist in Calendar does NOT update CRM, and CRM-side renames flow
        in via `crm_employee_id` linkage rather than this UI.

        When the WHOLE catalog is CRM-managed (catalog_version > 0) the note
        hardens: nothing is authored here, everything syncs in.
      */}
      <div
        role="note"
        aria-label="CRM ownership"
        className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
      >
        {managedByCrm
          ? "Managed in the CRM — edit services and doctors there; changes sync here automatically."
          : "Employees come from your CRM. Edit a stylist's name in CRM; equipment + rooms are managed here."}
      </div>
      {resources.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<Resource>
          rows={visible}
          getRowKey={(r) => r.id}
          selectedRowKey={selectedId}
          onRowClick={(r) => setSelectedId(r.id)}
          columns={[
            { header: "Name", cell: (r) => r.name },
            { header: "Role", cell: (r) => r.role },
            {
              header: "Freshness",
              cell: (r) => (
                <Badge variant={r.freshness_state === "fresh" ? "success" : "warning"}>
                  {r.freshness_state}
                </Badge>
              )
            },
            { header: "Home base", cell: (r) => (r.home_base ? `${r.home_base.lat.toFixed(3)}, ${r.home_base.lng.toFixed(3)}` : "—") },
            // Row edit/delete actions disappear entirely for CRM-managed
            // catalogs (read-only display stays).
            ...(managedByCrm ? [] : [{
              header: "",
              cell: (r: Resource) => (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(r)} aria-label={`Edit ${r.name}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${r.name}`}
                    onClick={async () => {
                      try {
                        await api.deleteResource(r.id);
                        toast({ title: "Archived", variant: "success" });
                        resources.reload();
                      } catch (error) {
                        toast({ title: "Could not delete", description: String(error), variant: "destructive" });
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )
            }])
          ]}
        />
      )}
      <ResourceDialog
        open={creating}
        onOpenChange={setCreating}
        mode="create"
        onSaved={() => {
          resources.reload();
          setCreating(false);
        }}
      />
      <ResourceDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        mode="edit"
        initial={editing}
        onSaved={() => {
          resources.reload();
          setEditing(null);
        }}
      />
    </div>
  );
}

function moveSelection<T extends { id: string }>(
  rows: T[],
  current: string | null,
  delta: 1 | -1
): string | null {
  if (rows.length === 0) return null;
  const idx = current ? rows.findIndex((r) => r.id === current) : -1;
  const nextIdx = idx === -1 ? (delta === 1 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, idx + delta));
  return rows[nextIdx]!.id;
}

function ResourceDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  initial?: Resource | null;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New resource" : `Edit ${initial?.name ?? "resource"}`}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const lat = Number(form.get("lat"));
            const lng = Number(form.get("lng"));
            const body: Partial<Resource> = {
              name: String(form.get("name") ?? ""),
              role: String(form.get("role") ?? "provider")
            };
            if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
              body.home_base = { lat, lng };
            } else if (mode === "edit" && initial?.home_base) {
              // Preserve existing home_base if user cleared the field; explicit null
              // handling can be added if backend accepts a `clear_home_base` flag.
              body.home_base = initial.home_base;
            }
            setSubmitting(true);
            try {
              if (mode === "edit" && initial) {
                await api.updateResource(initial.id, body);
                toast({ title: "Resource updated", variant: "success" });
              } else {
                await api.createResource({ ...body, freshness_state: "fresh" });
                toast({ title: "Resource created", variant: "success" });
              }
              onSaved();
            } catch (error) {
              toast({ title: "Could not save", description: String(error), variant: "destructive" });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <Field label="Name" name="name" required defaultValue={initial?.name ?? ""} />
          <Field label="Role" name="role" defaultValue={initial?.role ?? "provider"} />
          <Field label="Home base lat" name="lat" type="number" step="0.0001" defaultValue={initial?.home_base ? String(initial.home_base.lat) : ""} />
          <Field label="Home base lng" name="lng" type="number" step="0.0001" defaultValue={initial?.home_base ? String(initial.home_base.lng) : ""} />
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  name,
  ...rest
}: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} {...rest} />
    </div>
  );
}
